export type WithCaptchaToken<T extends object> = T & {
  captchaToken?: string;
};

export function attachCaptchaToken<T extends object>(
  body: T,
  captchaToken: string
): T & { captchaToken: string } {
  return { ...body, captchaToken };
}

function headersToRecord(headers?: HeadersInit): Record<string, string> {
  if (!headers) {
    return {};
  }

  if (headers instanceof Headers) {
    const record: Record<string, string> = {};
    headers.forEach((value, key) => {
      record[key] = value;
    });
    return record;
  }

  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }

  return { ...headers };
}

/**
 * ApiService.configureOptions spreads fetchOptions last and replaces `headers`.
 * Without Content-Type, fetch sends JSON string as text/plain.
 */
export function mergeCaptchaFetchOptions(
  captchaToken: string,
  fetchOptions?: RequestInit
): RequestInit {
  const prevHeaders = headersToRecord(fetchOptions?.headers);

  return {
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...prevHeaders,
      'X-Captcha-Token': captchaToken,
    },
  };
}

export function getCaptchaRequestParams<T extends WithCaptchaToken<object>>(
  body: T
): {
  body: Omit<T, 'captchaToken'>;
  fetchOptions?: RequestInit;
} {
  const { captchaToken, ...rest } = body;

  if (!captchaToken) {
    return { body: rest };
  }

  return {
    body: rest,
    fetchOptions: mergeCaptchaFetchOptions(captchaToken),
  };
}
